import { useMemo, useState } from 'react';
import { Plus, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@ncoder/ui';
import {
  useOrgRoles,
  useOrgMembers,
  useOrgServiceCatalog,
  useCreateOrgRole,
  useUpdateOrgRole,
  useDeleteOrgRole,
  useAssignMemberRole,
} from '@/hooks/org-admin';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { RoleRow } from '@/components/molecules/org-admin/RoleRow';
import { AssignRoleMembersDialog } from '@/components/molecules/org-admin/AssignRoleMembersDialog';
import {
  assignBlockedReason,
  membersAssignableToRole,
} from '@/components/molecules/org-admin/assignableMembers';
import { RoleFormModal, type RoleFormSubmitData } from '@/components/molecules/org-admin/RoleFormModal';
import {
  OrgAdminCardSkeleton,
  OrgAdminEmptyState,
  OrgAdminErrorState,
  OrgAdminNoResults,
} from '@/components/molecules/org-admin/OrgAdminStates';
import { useTextFilter } from '@/components/molecules/org-admin/OrgAdminTable';
import { OrgAdminResultCount, OrgAdminToolbar } from '@/components/molecules/org-admin/OrgAdminToolbar';
import { NO_PERMISSION_REASON, type OrgAdminSelfContext } from '@/components/molecules/org-admin/types';
import type { OrgRole } from '@/services/org-admin';

export interface RolesPanelProps {
  self: OrgAdminSelfContext;
}

type ModalState = { mode: 'create' } | { mode: 'edit'; role: OrgRole } | null;

/**
 * Progress across a multi-member assignment.
 *
 * `updateOrgUserRole` takes one member, so assigning five is five requests and a partial failure is
 * a real outcome — not an edge case. This is held in the panel rather than derived from the mutation
 * because a react-query mutation object describes the LAST request, and what the admin needs is the
 * tally across all of them.
 */
type AssignState = {
  role: OrgRole;
  progress: { done: number; total: number; failed: string[] } | null;
} | null;

export function RolesPanel({ self }: RolesPanelProps) {
  const { data: roles = [], isLoading, isError, error, refetch } = useOrgRoles();
  const { data: members = [] } = useOrgMembers();
  const { data: serviceCatalog = [] } = useOrgServiceCatalog();
  const createRole = useCreateOrgRole();
  const assignMemberRole = useAssignMemberRole();
  const updateRole = useUpdateOrgRole();
  const deleteRole = useDeleteOrgRole();
  const [confirmModal, confirmModalContext] = useConfirmModal();

  const [modalState, setModalState] = useState<ModalState>(null);
  const [assignState, setAssignState] = useState<AssignState>(null);
  const [query, setQuery] = useState('');

  // Roles stay a card grid rather than becoming a table: a role's substance is a variable-length
  // list of granted modules, which a fixed-width column can only truncate.
  const searched = useTextFilter(roles, query, (role) => [
    role.name,
    ...role.capabilities.map((capability) => capability.label),
  ]);

  const memberCountByRoleId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const member of members) {
      if (member.role_id) counts.set(member.role_id, (counts.get(member.role_id) ?? 0) + 1);
    }
    return counts;
  }, [members]);

  const isSubmitting = createRole.isPending || updateRole.isPending;

  const handleFormSubmit = async (data: RoleFormSubmitData) => {
    try {
      if (modalState?.mode === 'edit') {
        await updateRole.mutateAsync({ roleId: modalState.role.id, input: data });
        toast.success('Role updated');
      } else {
        await createRole.mutateAsync(data);
        toast.success('Role created');
      }
      setModalState(null);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to save role');
    }
  };

  /**
   * Assign several members to one role, reporting what actually happened.
   *
   * SEQUENTIAL, not `Promise.all`. Every request mutates the same members list, and the hook
   * invalidates that list on each success — firing them together means N overlapping refetches of a
   * query whose result is only correct after the last write lands. Sequential also means the tally
   * below is accurate at every step rather than only at the end.
   *
   * `mutateAsync` in a loop rather than `mutate`, because the loop needs to know each outcome. Each
   * failure is caught per member: one rejected assignment must not abandon the rest, since the admin
   * asked for all of them and the ones that can succeed should.
   */
  const handleAssignMembers = async (userIds: string[]) => {
    const role = assignState?.role;
    if (!role || userIds.length === 0) return;

    const failed: string[] = [];
    let done = 0;
    setAssignState({ role, progress: { done: 0, total: userIds.length, failed: [] } });

    for (const userId of userIds) {
      try {
        await assignMemberRole.mutateAsync({ userId, roleId: role.id });
        done += 1;
      } catch {
        // Identify the member by email, not by id: the id is meaningless to the person reading it.
        const member = members.find((candidate) => candidate.user_id === userId);
        failed.push(member?.email ?? userId);
      }
      setAssignState({ role, progress: { done, total: userIds.length, failed: [...failed] } });
    }

    if (failed.length === 0) {
      toast.success(done === 1 ? 'Member assigned' : `${done} members assigned`);
      setAssignState(null);
      return;
    }
    /*
     * Partial failure keeps the dialog OPEN with the per-member detail. A toast alone would be
     * dismissed in seconds, taking with it the only record of which people were not changed — and
     * the admin would leave believing the whole set moved.
     */
    toast.error(`${failed.length} of ${userIds.length} could not be assigned`);
  };

  const handleDelete = async (role: OrgRole) => {
    const confirmed = await confirmModal.error({
      title: `Delete “${role.name}”?`,
      content:
        'This permanently removes the role and the module access it grants. Members who held it keep their accounts but lose that access until you assign them another role.',
      okText: 'Delete role',
      cancelText: 'Keep it',
      okButtonProps: { variant: 'destructive' },
    });
    if (!confirmed) return;
    try {
      await deleteRole.mutateAsync(role.id);
      toast.success('Role deleted');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to delete role');
    }
  };

  const canFilter = !isLoading && !isError && roles.length > 0;

  const newRoleButton = (
    <Button
      size="sm"
      className="h-8"
      onClick={() => setModalState({ mode: 'create' })}
      disabled={!self.canManage}
      title={self.canManage ? undefined : NO_PERMISSION_REASON}
    >
      <Plus className="h-4 w-4" aria-hidden />
      New role
    </Button>
  );

  return (
    <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col">
      {/* Search only when there is something to search — see MembersPanel. */}
      <OrgAdminToolbar
        searchPlaceholder={canFilter ? 'Search roles by name or module' : undefined}
        searchValue={query}
        onSearchChange={canFilter ? setQuery : undefined}
        action={newRoleButton}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <OrgAdminCardSkeleton cards={6} />
        ) : isError ? (
          <OrgAdminErrorState title="Couldn't load roles" error={error} onRetry={() => void refetch()} />
        ) : roles.length === 0 ? (
          <OrgAdminEmptyState
            icon={ShieldCheck}
            title="No roles yet"
            description="A role bundles the modules a member may use. Create one, then assign it to people from the Members tab."
            action={newRoleButton}
          />
        ) : searched.length === 0 ? (
          <OrgAdminNoResults query={query} onClear={() => setQuery('')} />
        ) : (
          /*
           * `auto-fill` with a minimum track, not `sm:grid-cols-2 xl:grid-cols-3`.
           * The viewport-keyed version put two cards side by side whenever the *window* was ≥640px,
           * including when the panel itself had 380px because the sidebar was open — so the cards
           * were squeezed to 190px and every capability chip wrapped onto its own line.
           */
          <div className="grid gap-3 p-[var(--oa-gutter)] [grid-template-columns:repeat(auto-fill,minmax(min(260px,100%),1fr))]">
            {searched.map((role) => (
              <RoleRow
                key={role.id}
                role={role}
                memberCount={memberCountByRoleId.get(role.id) ?? 0}
                canManage={self.canManage}
                onEdit={(target) => setModalState({ mode: 'edit', role: target })}
                onDelete={handleDelete}
                onAssignMembers={(target) => setAssignState({ role: target, progress: null })}
                assignBlockedReason={assignBlockedReason(
                  self.currentUserRole,
                  role,
                  membersAssignableToRole(members, role).length,
                )}
                isDeleting={deleteRole.isPending && deleteRole.variables === role.id}
              />
            ))}
          </div>
        )}
      </div>

      {!isLoading && !isError && roles.length > 0 && (
        <OrgAdminResultCount shown={searched.length} total={roles.length} noun="role" />
      )}

      <RoleFormModal
        open={modalState !== null}
        mode={modalState?.mode ?? 'create'}
        initialRole={modalState?.mode === 'edit' ? modalState.role : null}
        serviceCatalog={serviceCatalog}
        canGrantAdmin={self.currentUserRole === 'owner'}
        isSubmitting={isSubmitting}
        onSubmit={handleFormSubmit}
        onClose={() => setModalState(null)}
      />
      <AssignRoleMembersDialog
        open={assignState !== null}
        role={assignState?.role ?? null}
        candidates={assignState ? membersAssignableToRole(members, assignState.role) : []}
        isSubmitting={assignMemberRole.isPending}
        progress={assignState?.progress ?? null}
        onSubmit={handleAssignMembers}
        onClose={() => setAssignState(null)}
      />
      {confirmModalContext}
    </div>
  );
}
